import { useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Camera, Trash, CircleNotch } from "@phosphor-icons/react";

const PHASES = { avant: "Avant (état initial)", actuel: "Actuel", apres: "Après travaux" };
const inputCls = "rounded-lg bg-zinc-950/70 border border-zinc-800 focus:border-amber-500/60 outline-none px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600";

/** Photos d'un logement : téléversement privé (stockage protégé, lien signé 24 h) + légende, date connue, source, phase. */
export function PhotosPanel({ logement, photos = [], onChanged }) {
  const fileRef = useRef(null);
  const [meta, setMeta] = useState({ legende: "", date_connue: "", source: "", phase: "actuel" });
  const [busy, setBusy] = useState(false);
  const [phaseF, setPhaseF] = useState("toutes");
  const mine = photos.filter((p) => p.logement === logement && (phaseF === "toutes" || p.phase === phaseF));
  const maj = async (p, patch) => {
    try { await api.put(`/brebeuf/photos/${p.id}`, patch); onChanged(photos.map((x) => (x.id === p.id ? { ...x, ...patch, logement_a_confirmer: patch.logement ? false : x.logement_a_confirmer } : x))); }
    catch { toast.error("Modification impossible"); }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("logement", logement);
    Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
    setBusy(true);
    try {
      const r = await api.post("/brebeuf/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChanged([...photos, r.data]);
      setMeta({ legende: "", date_connue: "", source: "", phase: "actuel" });
      toast.success("Photo versée au dossier (privé).");
    } catch (e) { toast.error(e?.response?.data?.detail || "Téléversement impossible"); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const remove = async (id) => {
    if (!window.confirm("Retirer cette photo du dossier ?")) return;
    try { await api.delete(`/brebeuf/photos/${id}`); onChanged(photos.filter((p) => p.id !== id)); } catch { toast.error("Suppression impossible"); }
  };

  return (
    <div data-testid={`photos-${logement}`} className="space-y-4">
      <div className="rounded-xl border border-dashed border-zinc-700 p-4 grid md:grid-cols-4 gap-2 items-end bg-zinc-900/40">
        <label className="text-xs text-zinc-400 md:col-span-2">Légende (pièce, ce qu'on voit)
          <input data-testid={`photo-legende-${logement}`} className={`${inputCls} w-full mt-1`} value={meta.legende} onChange={(e) => setMeta({ ...meta, legende: e.target.value })} placeholder="Salle de bain — trou dans le mur près de la porte" /></label>
        <label className="text-xs text-zinc-400">Date connue (sinon vide)
          <input data-testid={`photo-date-${logement}`} className={`${inputCls} w-full mt-1`} value={meta.date_connue} onChange={(e) => setMeta({ ...meta, date_connue: e.target.value })} placeholder="2026-10-23" /></label>
        <label className="text-xs text-zinc-400">Phase
          <select data-testid={`photo-phase-${logement}`} className={`${inputCls} w-full mt-1`} value={meta.phase} onChange={(e) => setMeta({ ...meta, phase: e.target.value })}>
            {Object.entries(PHASES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></label>
        <label className="text-xs text-zinc-400 md:col-span-3">Source (qui a pris la photo, d'où elle vient)
          <input data-testid={`photo-source-${logement}`} className={`${inputCls} w-full mt-1`} value={meta.source} onChange={(e) => setMeta({ ...meta, source: e.target.value })} placeholder="Téléphone de Ralph, visite du 23 oct. 2026" /></label>
        <div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`photo-file-${logement}`} />
          <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`photo-upload-${logement}`}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-sm px-3 py-2 disabled:opacity-50 transition-colors">
            {busy ? <CircleNotch className="animate-spin" /> : <Camera weight="bold" />} Ajouter une photo
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`photos-filtre-${logement}`}>
        {[["toutes", "Toutes"], ["avant", "Avant"], ["actuel", "Actuel"], ["apres", "Après travaux"]].map(([k, l]) => <button key={k} type="button" onClick={() => setPhaseF(k)} className={`rounded-full px-3 py-1 border ${phaseF === k ? "bg-amber-500/15 text-amber-200 border-amber-500/40" : "text-zinc-400 border-zinc-800 hover:text-zinc-100"}`}>{l}</button>)}
        <span className="text-zinc-500">{mine.length} photo(s)</span>
      </div>
      {mine.length === 0 ? (
        <p className="text-sm text-zinc-500 italic" data-testid={`photos-empty-${logement}`}>Aucune photo datée dans le dossier pour ce logement. Les photos jointes à la réclamation du 3 juillet et celles d'octobre 2026 sont à verser ici (date et source).</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {mine.map((p) => (
            <figure key={p.id} className="rounded-xl overflow-hidden border border-zinc-800 bg-zinc-900/60" data-testid={`photo-card-${p.id}`}>
              <img src={p.url} alt={p.legende || "Photo"} className="w-full h-44 object-cover" loading="lazy" />
              <figcaption className="p-3 text-xs text-zinc-300 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="uppercase tracking-wider text-[10px] font-bold text-amber-300">{PHASES[p.phase] || p.phase}</span>
                  <button type="button" onClick={() => remove(p.id)} className="text-zinc-600 hover:text-red-400" aria-label="Retirer" data-testid={`photo-delete-${p.id}`}><Trash /></button>
                </div>
                <div className="text-zinc-100">{p.legende || <span className="italic text-zinc-500">sans légende</span>}</div>
                <div className="text-zinc-500">Date connue : {p.date_connue || "inconnue"} · Source : {p.source}</div>
                <div className="flex items-center gap-2 pt-1">
                  {p.logement_a_confirmer && <span className="text-amber-300 font-bold">Logement à confirmer :</span>}
                  <button type="button" onClick={() => maj(p, { logement: logement === "876" ? "878" : "876" })} className="text-sky-300 hover:text-sky-200" data-testid={`photo-move-${p.id}`}>→ déplacer au {logement === "876" ? "878" : "876"}</button>
                  <select value={p.phase} onChange={(e) => maj(p, { phase: e.target.value })} className="bg-zinc-950/70 border border-zinc-800 rounded px-1 py-0.5 text-[11px] text-zinc-200" data-testid={`photo-phase-select-${p.id}`}>{Object.entries(PHASES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}
